/**
 * Driver Monitor Page
 * Real-time driver position monitoring with map visualization
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Button, Badge } from '../components/ui';
import { MapPin, Activity, RefreshCw } from 'lucide-react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  getDriverPositions,
  getDriverPositionHistory,
  getDriverRoutes,
  type DriverWithPosition,
  type DriverPosition
} from '../services/driverMonitor.api';
import type { Route, RouteStop } from '../types';
import { cn } from '../utils/cn';
import { setMapLanguageToArabic, ARABIC_MAP_STYLE } from '../utils/mapbox';
import { isValidCoordinate } from '../utils/coordinates';
import { getBaghdadToday } from '../utils/date';

// Route colors for visualization
const ROUTE_COLORS = [
  '#3B82F6', '#2563EB', '#10B981', '#F59E0B', '#EF4444',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
];

const EVENT_CATEGORIES = {
  PARENT: [
    'START_TASK_DRIVING_PARENT',
    'ARRIVE_TASK_ARRIVED_PARENT',
    'COMPLETE_TASK_DONE_PARENT',
    'START_TASK_ARRIVED_PARENT',
    'COMPLETE_TASK_LIST',
    'ARRIVE_TASK_IN_PROGRESS_PARENT',
    'START_TASK_ASSIGNED_PARENT',
    'ARRIVE_TASK_DRIVING_PARENT',
    'ARRIVE_TASK_ASSIGNED_PARENT',
    'COMPLETE_TASK_IN_PROGRESS_PARENT',
    'ARRIVED_PARENT_PARENT',
    'COMPLETE_DONE_PARENT_PARENT',
    'COMPLETE_ALL_TASKS_DONE_PARENT',
    'COMPLETE_SUBTASK_IN_PROGRESS_PARENT'
  ],
  PICKUP: [
    'OPEN_CAMERA_SCANNER_IN_PROGRESS_PICK_UP_ORDERS',
    'SCAN_ORDER_IN_PROGRESS_PICK_UP_ORDERS',
    'COMPLETE_SUBTASK_IN_PROGRESS_PICK_UP_ORDERS',
    'REMOVE_ORDER_IN_PROGRESS_PICK_UP_ORDERS',
    'OPEN_EXTERNAL_SCANNER_IN_PROGRESS_PICK_UP_ORDERS',
    'OPEN_CAMERA_SCANNER_ARRIVED_PICK_UP_ORDERS',
    'SCAN_ORDER_ARRIVED_PICK_UP_ORDERS',
    'COMPLETE_SUBTASK_ARRIVED_PICK_UP_ORDERS',
    'REMOVE_ORDER_DONE_PICK_UP_ORDERS',
    'OPEN_SCANNER_IN_PROGRESS_PICKUP-ORDERS',
    'COMPLETE_SUBTASK_IN_PROGRESS_PICKUP-ORDERS',
    'OPEN_EXTRA_SCANNER_IN_PROGRESS_PICKUP-ORDERS',
    'OPEN_SCANNER_DONE_PICKUP-ORDERS',
    'OPEN_SCANNER_ARRIVED_PICKUP-ORDERS',
    'OPEN_SCANNER_NEW_PICKUP-ORDERS'
  ],
  RTO: [
    'COMPLETE_SUBTASK_IN_PROGRESS_DROP_OFF_RTO',
    'COMPLETE_SUBTASK_IN_PROGRESS_PICK_UP_RTO',
    'OPEN_CAMERA_SCANNER_IN_PROGRESS_PICK_UP_RTO',
    'SCAN_ORDER_IN_PROGRESS_PICK_UP_RTO',
    'OPEN_EXTERNAL_SCANNER_IN_PROGRESS_PICK_UP_RTO',
    'COMPLETE_SUBTASK_DONE_DROP_OFF_RTO',
    'COMPLETE_SUBTASK_DONE_PICK_UP_RTO',
    'COMPLETE_SUBTASK_ARRIVED_DROP_OFF_RTO',
    'OPEN_CAMERA_SCANNER_ARRIVED_PICK_UP_RTO',
    'COMPLETE_SUBTASK_ARRIVED_PICK_UP_RTO',
    'OPEN_CAMERA_SCANNER_DONE_PICK_UP_RTO',
    'OPEN_SCANNER_IN_PROGRESS_PICKUP-RTO',
    'COMPLETE_SUBTASK_IN_PROGRESS_PICKUP-RTO',
    'COMPLETE_SUBTASK_IN_PROGRESS_DROPOFF-RTO',
    'UPLOAD_PROOF_IN_PROGRESS_DROPOFF-RTO',
    'OPEN_EXTRA_SCANNER_IN_PROGRESS_PICKUP-RTO',
    'OPEN_SCANNER_DONE_PICKUP-RTO',
    'OPEN_SCANNER_ARRIVED_PICKUP-RTO'
  ],
  WAREHOUSE: [
    'COMPLETE_SUBTASK_IN_PROGRESS_DROP_OFF_ORDER_TO_WAREHOUSE',
    'COMPLETE_SUBTASK_ARRIVED_DROP_OFF_ORDER_TO_WAREHOUSE',
    'UPLOAD_PROOF_IN_PROGRESS_DROPOFF-WAREHOUSE',
    'COMPLETE_SUBTASK_IN_PROGRESS_DROPOFF-WAREHOUSE',
    'COMPLETE_SUBTASK_DONE_DROP_OFF_ORDER_TO_WAREHOUSE',
    'UPLOAD_PROOF_ARRIVED_DROPOFF-WAREHOUSE'
  ],
  CASH: [
    'OPEN_CAMERA_SCANNER_IN_PROGRESS_PICK_UP_CASH',
    'COMPLETE_SUBTASK_DONE_PICK_UP_CASH',
    'OPEN_CAMERA_SCANNER_IN_PROGRESS_DROP_OFF_CASH',
    'COMPLETE_SUBTASK_IN_PROGRESS_DROP_OFF_CASH',
    'COMPLETE_SUBTASK_IN_PROGRESS_PICK_UP_CASH',
    'OPEN_EXTERNAL_SCANNER_IN_PROGRESS_DROP_OFF_CASH',
    'OPEN_EXTERNAL_SCANNER_IN_PROGRESS_PICK_UP_CASH',
    'OPEN_SCANNER_IN_PROGRESS_PICKUP-CASH',
    'UPLOAD_PROOF_IN_PROGRESS_DROPOFF-CASH',
    'COMPLETE_SUBTASK_IN_PROGRESS_DROPOFF-CASH',
    'OPEN_SCANNER_IN_PROGRESS_DROPOFF-CASH',
    'OPEN_EXTRA_SCANNER_IN_PROGRESS_PICKUP-CASH',
    'OPEN_SCANNER_DONE_PICKUP-CASH',
    'OPEN_EXTRA_SCANNER_IN_PROGRESS_DROPOFF-CASH'
  ]
};

const ALL_FILTER_EVENTS = Object.values(EVENT_CATEGORIES).flat();


const CATEGORY_STYLES: Record<string, { color: string; icon: string; label: string }> = {
  PARENT: { color: '#3b82f6', icon: '🚗', label: 'Trip' },
  PICKUP: { color: '#10b981', icon: '📦', label: 'Pickup' },
  RTO: { color: '#f59e0b', icon: '↩️', label: 'RTO' },
  WAREHOUSE: { color: '#6366f1', icon: '🏢', label: 'Warehouse' },
  CASH: { color: '#ef4444', icon: '💵', label: 'Cash' },
  UNKNOWN: { color: '#6b7280', icon: '📍', label: 'Other' }
};




/**
 * Route ids to draw. Drafts and cancelled routes are never drawn (not given to the driver).
 * With a selected driver: all of that driver's routes. Otherwise: routes of drivers with positions.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper, exported for its test
export function visibleRoutesFor(
  routes: Route[],
  driverUidsWithPositions: Set<string>,
  selectedUid: string | null
): Set<string> {
  return new Set(
    routes
      .filter(r => r.status !== 'draft' && r.status !== 'cancelled')
      .filter(r => r.driver_uid && (selectedUid ? r.driver_uid === selectedUid : driverUidsWithPositions.has(r.driver_uid)))
      .map(r => r._id)
  );
}

/** Popup content built from DOM nodes + textContent so server text is never parsed as HTML. */
function textNode(tag: string, text: string, css = ''): HTMLElement {
  const el = document.createElement(tag);
  el.textContent = text;
  if (css) el.style.cssText = css;
  return el;
}

export default function DriverMonitor() {
  // Map refs
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const [mapLoaded, setMapLoaded] = useState(false);
  const positionRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Date/time range state
  const [selectedDate, setSelectedDate] = useState<string>(
    getBaghdadToday()
  );
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | 'custom'>('24h');

  // Data state
  const [drivers, setDrivers] = useState<DriverWithPosition[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [selectedDriverUid, setSelectedDriverUid] = useState<string | null>(null);
  const [driverHistory, setDriverHistory] = useState<Map<string, DriverPosition[]>>(new Map());

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleRouteIds, setVisibleRouteIds] = useState<Set<string>>(new Set());
  const [showPositionHistory, setShowPositionHistory] = useState<Set<string>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [showPositionPanel, setShowPositionPanel] = useState(false);
  const [showAllPositions, setShowAllPositions] = useState(false);

  // Event filtering state
  const [selectedEvents, setSelectedEvents] = useState<string[]>(() => {
    const saved = localStorage.getItem('driverMonitorEventsFilter');
    return saved ? JSON.parse(saved) : ALL_FILTER_EVENTS;
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    const now = new Date();
    const date = new Date(selectedDate);

    let startDate: Date;
    let endDate: Date;

    if (timeRange === '1h') {
      endDate = now;
      startDate = new Date(now.getTime() - 60 * 60 * 1000);
    } else if (timeRange === '24h') {
      endDate = now;
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    } else {
      // Custom: from 6 PM previous day to 6 AM next day
      startDate = new Date(date);
      startDate.setHours(18, 0, 0, 0);
      startDate.setDate(startDate.getDate() - 1);

      endDate = new Date(date);
      endDate.setHours(6, 0, 0, 0);
      endDate.setDate(endDate.getDate() + 1);
    }

    return { startDate, endDate };
  }, [selectedDate, timeRange]);

  // Fetch position history for a driver
  const fetchDriverHistory = useCallback(async (driverUid: string) => {
    try {
      const history = await getDriverPositionHistory(
        driverUid,
        dateRange.startDate,
        dateRange.endDate
      );
      setDriverHistory(prev => {
        const next = new Map(prev);
        next.set(driverUid, history);
        return next;
      });
    } catch (err) {
      console.error('Failed to fetch driver history:', err);
    }
  }, [dateRange.startDate, dateRange.endDate]);

  // Fetch driver positions and routes
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [driversData, routesData] = await Promise.all([
        getDriverPositions(dateRange.startDate, dateRange.endDate),
        getDriverRoutes(new Date(selectedDate)),
      ]);

      setDrivers(driversData);
      setRoutes(routesData);
      setLastRefresh(new Date());

      // If a driver is selected, refresh their history too
      if (selectedDriverUid) {
        fetchDriverHistory(selectedDriverUid);
      }

      // Auto-show routes for drivers with positions
      const driverUidsWithPositions = new Set(driversData.map(d => d.driver_uid));
      setVisibleRouteIds(visibleRoutesFor(routesData, driverUidsWithPositions, selectedDriverUid));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch driver data');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange.startDate, dateRange.endDate, selectedDate, selectedDriverUid, fetchDriverHistory]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Clear history cache when date range changes
  useEffect(() => {
    setDriverHistory(new Map());
  }, [dateRange]);

  // Handle driver selection
  const handleDriverClick = useCallback((driverUid: string) => {
    if (selectedDriverUid === driverUid) {
      // Deselect: close panel, hide positions and route
      setSelectedDriverUid(null);
      setShowPositionPanel(false);
      setShowPositionHistory(new Set()); // Clear all trails
      setVisibleRouteIds(new Set()); // Clear all routes
    } else {
      // Select: open panel, show only THIS driver's positions and route
      setSelectedDriverUid(driverUid);
      setShowPositionPanel(true);

      // Reset showPositionHistory to only include the new driver
      const newHistorySet = new Set<string>();
      newHistorySet.add(driverUid);
      setShowPositionHistory(newHistorySet);

      // Only this driver's routes (fetchData applies the same rule on the refetch this click triggers)
      setVisibleRouteIds(visibleRoutesFor(routes, new Set(), driverUid));

      // Fetch history if not already loaded
      if (!driverHistory.has(driverUid)) {
        fetchDriverHistory(driverUid);
      }
    }
  }, [selectedDriverUid, driverHistory, fetchDriverHistory, routes]);


  // Helper to parse database date strings as UTC
  const parseDate = useCallback((dateStr: string) => {
    if (!dateStr) return new Date();
    // If it already has a timezone indicator, parse it as is
    if (dateStr.includes('Z') || dateStr.includes('+')) return new Date(dateStr);
    // Otherwise, assume it's UTC from the database and handle MySQL format (space instead of T)
    const isoStr = dateStr.includes(' ') ? dateStr.replace(' ', 'T') : dateStr;
    return new Date(`${isoStr}Z`);
  }, []);

  // Format time (Baghdad Time)
  const formatTime = useCallback((dateStr: string) => {
    return parseDate(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Asia/Baghdad',
    });
  }, [parseDate]);

  // Format time ago (Baghdad Time context)
  const formatTimeAgo = useCallback((dateStr: string) => {
    const date = parseDate(dateStr);
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }, [parseDate]);

  // Get driver's active route
  const getDriverRoute = useCallback((driverUid: string) => {
    return routes.find(r => r.driver_uid === driverUid);
  }, [routes]);


  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    const timer = setTimeout(() => {
      if (!mapContainer.current) return;

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: ARABIC_MAP_STYLE,
        center: [44.448563, 33.284412],
        zoom: 11,
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
      
      // Set Arabic language for map labels
      setMapLanguageToArabic(map.current);
      
      map.current.on('load', () => {
        setMapLoaded(true);
        map.current?.resize();
      });
    }, 100);

    return () => {
      clearTimeout(timer);
      map.current?.remove();
      map.current = null;
    };
  }, []);

  // Helper to get event category
  const getEventCategory = useCallback((event: string): string => {
    for (const [category, events] of Object.entries(EVENT_CATEGORIES)) {
      if (events.some(e => event.includes(e))) return category;
    }
    return 'UNKNOWN';
  }, []);



  // Render map markers and routes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    if (map.current.getLayer('route-lines')) map.current.removeLayer('route-lines');
    if (map.current.getLayer('position-trails')) map.current.removeLayer('position-trails');
    if (map.current.getSource('routes')) map.current.removeSource('routes');
    if (map.current.getSource('position-trails')) map.current.removeSource('position-trails');

    const bounds = new mapboxgl.LngLatBounds();
    let hasCoordinates = false;

    const trailFeatures: GeoJSON.Feature[] = [];

    // Determine which drivers to show positions for
    const driversToShowPositions = showAllPositions
      ? new Set(drivers.map(d => d.driver_uid))
      : showPositionHistory;

    driversToShowPositions.forEach(driverUid => {
      const allHistory = driverHistory.get(driverUid);
      if (!allHistory || allHistory.length === 0) return;

      // Apply event filtering
      const history = allHistory.filter(pos => selectedEvents.includes(pos.event));
      if (history.length === 0) return;

      const driver = drivers.find(d => d.driver_uid === driverUid);
      const driverIndex = drivers.findIndex(d => d.driver_uid === driverUid);
      const routeColor = ROUTE_COLORS[driverIndex % ROUTE_COLORS.length];

      // Draw the thin line connecting positions (filter invalid coordinates)
      const coordinates = history
        .filter(pos => isValidCoordinate(pos.lat, pos.lng))
        .map(pos => [pos.lng, pos.lat]);
      if (coordinates.length >= 2) {
        trailFeatures.push({
          type: 'Feature',
          properties: { color: routeColor, driverName: driver?.driver_name || 'Unknown' },
          geometry: {
            type: 'LineString',
            coordinates,
          },
        });
      }

      // Render individual position markers (filter invalid coordinates)
      const overlapMap = new Map<string, number>();

      history.forEach((pos, idx) => {
        if (!isValidCoordinate(pos.lat, pos.lng)) {
          return;
        }

        const cat = getEventCategory(pos.event);
        const style = CATEGORY_STYLES[cat];

        // Offset for visibility if multiple markers are at the same spot
        const posKey = `${pos.lat.toFixed(6)},${pos.lng.toFixed(6)}`;
        const overlapCount = overlapMap.get(posKey) || 0;
        overlapMap.set(posKey, overlapCount + 1);

        let offsetLng = pos.lng;
        let offsetLat = pos.lat;

        if (overlapCount > 0) {
          const angle = (overlapCount * 137.5) * (Math.PI / 180);
          const distance = overlapCount * 0.00006; // Subtle spiral offset
          offsetLng += distance * Math.cos(angle);
          offsetLat += distance * Math.sin(angle);
        }

        const el = document.createElement('div');
        el.className = 'custom-pos-marker';
        el.innerHTML = `
          <div style="
            width: 24px;
            height: 24px;
            background: ${style.color};
            border: 2px solid white;
            border-radius: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.3);
            cursor: pointer;
            transition: transform 0.2s;
          " onmouseover="this.style.transform='scale(1.2)'" onmouseout="this.style.transform='scale(1)'">
            ${style.icon}
          </div>
        `;

        const popupNode = document.createElement('div');
        popupNode.style.cssText = 'padding: 10px; min-width: 180px; font-family: system-ui, -apple-system, sans-serif;';
        popupNode.appendChild(textNode('div', pos.activity?.merchant_name || 'Task Location',
          'font-weight: 800; font-size: 13px; color: #111827; margin-bottom: 4px; border-bottom: 1px solid #f3f4f6; padding-bottom: 4px;'));
        const detail = document.createElement('div');
        detail.style.cssText = 'font-size: 11px; color: #374151; margin-top: 6px;';
        const catRow = document.createElement('div');
        catRow.style.cssText = 'display: flex; align-items: center; gap: 6px; margin-bottom: 4px;';
        catRow.appendChild(textNode('span', style.icon, 'font-size: 14px;'));
        catRow.appendChild(textNode('span', style.label, `font-weight: 600; color: ${style.color};`));
        detail.appendChild(catRow);
        detail.appendChild(textNode('div', pos.event.split('_').join(' '), 'font-weight: 500; color: #1f2937; margin-bottom: 2px;'));
        detail.appendChild(textNode('div', `🕒 ${formatTime(pos.created_at)}`, 'color: #6b7280; font-size: 10px;'));
        popupNode.appendChild(detail);

        const popup = new mapboxgl.Popup({ offset: 12, closeButton: false }).setDOMContent(popupNode);

        const marker = new mapboxgl.Marker({ element: el })
          .setLngLat([offsetLng, offsetLat])
          .setPopup(popup)
          .addTo(map.current!);

        el.addEventListener('click', () => {
          const positionKey = `${driverUid}-${idx}`;
          const positionElement = positionRefs.current.get(positionKey);
          if (positionElement) {
            positionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            positionElement.classList.add('ring-2', 'ring-blue-500');
            setTimeout(() => positionElement.classList.remove('ring-2', 'ring-blue-500'), 2000);
          }
        });

        markersRef.current.push(marker);
        bounds.extend([pos.lng, pos.lat]);
        hasCoordinates = true;
      });
    });

    if (trailFeatures.length > 0) {
      map.current.addSource('position-trails', {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: trailFeatures,
        },
      });

      map.current.addLayer({
        id: 'position-trails',
        type: 'line',
        source: 'position-trails',
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [2, 2],
        },
      });
    }

    // Also render route plan stops if any
    const routeFeatures: GeoJSON.Feature[] = [];
    routes.forEach((route, index) => {
      if (!visibleRouteIds.has(route._id)) return;
      const routeWithStops = route as Route & { stops?: RouteStop[] };
      if (!routeWithStops.stops?.length) return;

      const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
      const coordinates = routeWithStops.stops
        .filter(stop => stop.lat && stop.lng && isValidCoordinate(stop.lat, stop.lng))
        .map(stop => [stop.lng!, stop.lat!]);

      if (coordinates.length < 2) return;

      routeFeatures.push({
        type: 'Feature',
        properties: { color, driverName: route.driver_name || 'Unassigned' },
        geometry: { type: 'LineString', coordinates },
      });

      let drawnIdx = 0;
      routeWithStops.stops.forEach((stop) => {
        if (!stop.lat || !stop.lng || !isValidCoordinate(stop.lat, stop.lng)) return;
        drawnIdx += 1;
        const stopEl = document.createElement('div');
        stopEl.innerHTML = `
          <div style="width: 24px; height: 24px; background: white; border: 3px solid ${color}; border-radius: 50%;
               display: flex; align-items: center; justify-content: center; color: ${color}; font-weight: 800; font-size: 11px; box-shadow: 0 2px 5px rgba(0,0,0,0.2);">
            ${drawnIdx}
          </div>
        `;
        const stopPopup = document.createElement('div');
        stopPopup.appendChild(textNode('strong', `Plan Stop #${drawnIdx}`));
        stopPopup.appendChild(document.createElement('br'));
        stopPopup.appendChild(document.createTextNode(stop.merchant_name || (stop.type === 'depot' ? 'Depot' : stop.type)));
        const marker = new mapboxgl.Marker({ element: stopEl })
          .setLngLat([stop.lng, stop.lat])
          .setPopup(new mapboxgl.Popup().setDOMContent(stopPopup))
          .addTo(map.current!);
        markersRef.current.push(marker);
        bounds.extend([stop.lng, stop.lat]);
        hasCoordinates = true;
      });
    });

    if (routeFeatures.length > 0) {
      map.current.addSource('routes', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: routeFeatures },
      });
      map.current.addLayer({
        id: 'route-lines',
        type: 'line',
        source: 'routes',
        paint: { 'line-color': ['get', 'color'], 'line-width': 4, 'line-opacity': 0.4 },
      });
    }

    if (hasCoordinates) {
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 15 });
    }
  }, [mapLoaded, drivers, routes, visibleRouteIds, showPositionHistory, driverHistory, selectedDriverUid, fetchDriverHistory, formatTime, formatTimeAgo, showAllPositions]);

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Driver Monitor</h1>
            <p className="text-sm text-gray-500 mt-1">
              Real-time driver position tracking • {drivers.length} active drivers
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Time Range Selector */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTimeRange('1h')}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-lg transition-colors',
                  timeRange === '1h'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Last Hour
              </button>
              <button
                onClick={() => setTimeRange('24h')}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-lg transition-colors',
                  timeRange === '24h'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Last 24h
              </button>
              <button
                onClick={() => setTimeRange('custom')}
                className={cn(
                  'px-3 py-1.5 text-sm rounded-lg transition-colors',
                  timeRange === 'custom'
                    ? 'bg-blue-100 text-blue-700 font-medium'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                )}
              >
                Custom
              </button>
            </div>

            <div className="w-px h-6 bg-gray-200" />

            {/* Event Filter */}
            <div className="relative" ref={filterDropdownRef}>
              <button
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg transition-colors",
                  selectedEvents.length === ALL_FILTER_EVENTS.length
                    ? "bg-white text-gray-700 hover:bg-gray-50"
                    : "bg-blue-50 text-blue-700 border-blue-200"
                )}
              >
                <Activity className="h-4 w-4" />
                <span>
                  {selectedEvents.length === ALL_FILTER_EVENTS.length
                    ? "All Events"
                    : `${selectedEvents.length} Events`}
                </span>
                <svg className={cn("h-4 w-4 transition-transform", filterDropdownOpen && "rotate-180")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {filterDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 flex flex-col max-h-[70vh]">
                  <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-gray-50 rounded-t-xl">
                    <span className="text-xs font-bold text-gray-500 uppercase">Filter Events</span>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setSelectedEvents(ALL_FILTER_EVENTS);
                          localStorage.setItem('driverMonitorEventsFilter', JSON.stringify(ALL_FILTER_EVENTS));
                        }}
                        className="text-[10px] text-blue-600 font-bold hover:underline"
                      >
                        All
                      </button>
                      <button
                        onClick={() => {
                          setSelectedEvents([]);
                          localStorage.setItem('driverMonitorEventsFilter', JSON.stringify([]));
                        }}
                        className="text-[10px] text-red-600 font-bold hover:underline"
                      >
                        None
                      </button>
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto p-2">
                    {Object.entries(EVENT_CATEGORIES).map(([category, events]) => (
                      <div key={category} className="mb-4 last:mb-0">
                        <div className="flex items-center gap-2 px-2 mb-1">
                          <span className="text-lg">{CATEGORY_STYLES[category]?.icon}</span>
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">{CATEGORY_STYLES[category]?.label}</span>
                        </div>
                        <div className="space-y-0.5">
                          {events.map(event => (
                            <label key={event} className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg cursor-pointer group">
                              <input
                                type="checkbox"
                                checked={selectedEvents.includes(event)}
                                onChange={(e) => {
                                  const newSelection = e.target.checked
                                    ? [...selectedEvents, event]
                                    : selectedEvents.filter(ev => ev !== event);
                                  setSelectedEvents(newSelection);
                                  localStorage.setItem('driverMonitorEventsFilter', JSON.stringify(newSelection));
                                }}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-[11px] text-gray-700 font-medium break-all leading-tight group-hover:text-gray-900 transition-colors">
                                {event.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="w-px h-6 bg-gray-200" />


            {/* Date Picker */}
            {timeRange === 'custom' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            )}

            {/* Show All Positions Toggle */}
            <button
              onClick={() => {
                const newValue = !showAllPositions;
                setShowAllPositions(newValue);
                // Fetch history for all drivers if showing all
                if (newValue) {
                  drivers.forEach(driver => {
                    if (!driverHistory.has(driver.driver_uid)) {
                      fetchDriverHistory(driver.driver_uid);
                    }
                  });
                }
              }}
              className={cn(
                'px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 transition-colors',
                showAllPositions
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-gray-100 text-gray-600'
              )}
            >
              <MapPin className="h-4 w-4" />
              {showAllPositions ? 'Hide All Positions' : 'Show All Positions'}
            </button>

            {/* Auto-refresh Toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-lg flex items-center gap-2 transition-colors',
                autoRefresh
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-600'
              )}
            >
              <Activity className={cn('h-4 w-4', autoRefresh && 'animate-pulse')} />
              {autoRefresh ? 'Live' : 'Paused'}
            </button>

            {/* Manual Refresh */}
            <Button
              onClick={fetchData}
              disabled={isLoading}
              variant="secondary"
              size="sm"
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
              Refresh
            </Button>

            {/* Last Refresh Time */}
            <span className="text-xs text-gray-500">
              Updated {formatTimeAgo(lastRefresh.toISOString())}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Driver List Panel */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900">Active Drivers</h2>
            <p className="text-xs text-gray-500 mt-1">
              Showing {drivers.length} drivers with recent activity
            </p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {error && (
              <div className="p-4">
                <Card className="bg-red-50 border-red-200">
                  <p className="text-sm text-red-600">{error}</p>
                </Card>
              </div>
            )}

            {isLoading && drivers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                Loading drivers...
              </div>
            ) : drivers.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                <p>No active drivers found</p>
                <p className="text-xs mt-1">Drivers will appear when they send positions</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {drivers.map((driver) => {
                  const route = getDriverRoute(driver.driver_uid);
                  const isSelected = selectedDriverUid === driver.driver_uid;
                  // Use driver_uid as the unique index for stable selection
                  const driverIndex = drivers.findIndex(d => d.driver_uid === driver.driver_uid);
                  const routeColor = route ? ROUTE_COLORS[driverIndex % ROUTE_COLORS.length] : '#6B7280';

                  return (
                    <div
                      key={driver.driver_uid}
                      className={cn(
                        'p-4 cursor-pointer transition-all duration-200 border-l-4',
                        isSelected
                          ? 'bg-blue-50 border-blue-500 shadow-sm ring-1 ring-blue-100'
                          : 'bg-white border-transparent hover:bg-gray-50 hover:border-gray-200'
                      )}
                      onClick={() => handleDriverClick(driver.driver_uid)}
                    >
                      {/* Driver Info */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 shadow-sm"
                            style={{ background: routeColor }}>
                            {driver.driver_name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p dir="auto" className="font-bold text-gray-900 text-sm break-words">{driver.driver_name}</p>
                          </div>
                        </div>

                        <div className="flex flex-col items-end flex-shrink-0">
                          <div className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                            <MapPin className="h-3 w-3" />
                            {driver.position_count}
                          </div>
                          {driver.latest_position && (
                            <div className="flex flex-col items-end mt-1">
                              <span className="text-[10px] font-medium text-gray-700">
                                {formatTime(driver.latest_position.created_at)}
                              </span>
                              <span className="text-[10px] text-gray-500">
                                {formatTimeAgo(driver.latest_position.created_at)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="flex-1 h-full relative">
          <div ref={mapContainer} className="absolute inset-0 w-full h-full" />
        </div>

        {/* Position Detail Panel - Slide in from right */}
        {showPositionPanel && selectedDriverUid && (
          <div className="absolute top-0 right-0 h-full w-96 bg-white shadow-2xl border-l border-gray-200 flex flex-col animate-slide-in-right z-10">
            {/* Panel Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold shadow-md">
                  {drivers.find(d => d.driver_uid === selectedDriverUid)?.driver_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {drivers.find(d => d.driver_uid === selectedDriverUid)?.driver_name}
                  </h3>
                  <p className="text-xs text-gray-600">Position History</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowPositionPanel(false);
                  setSelectedDriverUid(null);
                }}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Position Count Summary */}
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">Total Positions:</span>
                <span className="font-semibold text-gray-900">
                  {driverHistory.get(selectedDriverUid)?.length || 0}
                </span>
              </div>
            </div>

            {/* Position List */}
            <div className="flex-1 overflow-y-auto">
              {(() => {
                const allHistory = driverHistory.get(selectedDriverUid);
                const history = allHistory?.filter(pos => selectedEvents.includes(pos.event)) || [];

                if (history.length > 0) {
                  return (
                    <div className="divide-y divide-gray-100">
                      {[...history].reverse().map((position) => {
                        const originalIndex = allHistory?.findIndex(p => p.uid === position.uid) ?? 0;
                        const positionKey = `${selectedDriverUid}-${originalIndex}`;
                        return (

                          <div
                            key={`${position.uid}-${originalIndex}`}
                            ref={(el) => {
                              if (el) {
                                positionRefs.current.set(positionKey, el);
                              } else {
                                positionRefs.current.delete(positionKey);
                              }
                            }}
                            className="p-4 hover:bg-blue-50 transition-all duration-200 cursor-pointer border-l-2 border-transparent hover:border-blue-500"
                            onClick={() => {
                              if (map.current) {
                                // Calculate offset for overlapping markers (same logic as marker creation)
                                const posKey = `${position.lat.toFixed(6)},${position.lng.toFixed(6)}`;
                                const history = driverHistory.get(selectedDriverUid)!;
                                let overlapCount = 0;
                                for (let i = 0; i < originalIndex; i++) {
                                  const prevKey = `${history[i].lat.toFixed(6)},${history[i].lng.toFixed(6)}`;
                                  if (prevKey === posKey) overlapCount++;
                                }

                                const angle = (overlapCount * 137.5) * (Math.PI / 180);
                                const distance = overlapCount * 0.00008;
                                const offsetLng = position.lng + distance * Math.cos(angle);
                                const offsetLat = position.lat + distance * Math.sin(angle);

                                map.current.flyTo({
                                  center: [offsetLng, offsetLat],
                                  zoom: 16,
                                  duration: 1000,
                                  essential: true
                                });
                              }
                            }}
                          >
                            {/* Event Badge - Full Width at Top */}
                            <div className="mb-2">
                              <div className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-[10px] font-medium truncate text-center">
                                {position.event}
                              </div>
                            </div>

                            {/* Position Number and Time */}
                            <div className="flex items-center gap-2 mb-2">
                              <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
                                {originalIndex + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900">
                                  {formatTime(position.created_at)}
                                </p>
                                <p className="text-[10px] text-gray-500">
                                  {formatTimeAgo(position.created_at)}
                                </p>
                              </div>
                            </div>

                            {/* Location */}
                            <div className="flex items-start gap-2 mb-2 text-xs">
                              <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                              <div className="text-gray-600 flex-1 min-w-0">
                                <span className="font-mono text-[10px] break-all">{position.lat.toFixed(6)}, {position.lng.toFixed(6)}</span>
                              </div>
                            </div>

                            {/* Activity Details */}
                            {position.activity && (
                              <div className="mt-2 p-2 bg-gray-50 rounded-lg border border-gray-200 space-y-1.5">
                                {position.activity.merchant_name && (
                                  <p className="text-xs font-semibold text-gray-900 truncate">
                                    {position.activity.merchant_name}
                                  </p>
                                )}
                                {position.activity.address_text && (
                                  <p className="text-[10px] text-gray-600 line-clamp-2 leading-snug">
                                    {position.activity.address_text}
                                  </p>
                                )}
                                {position.activity.platform_code && (
                                  <div className="flex items-center gap-1.5">
                                    <span className="text-[10px] text-gray-500">Order:</span>
                                    <span className="text-[10px] font-mono font-semibold text-gray-900">
                                      {position.activity.platform_code}
                                    </span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5 flex-wrap pt-1">
                                  {position.activity.task_type && (
                                    <Badge variant="info" size="sm">
                                      {position.activity.task_type === 'pickup' ? '📦' : position.activity.task_type === 'cash' ? '💵' : '🚚'} {position.activity.task_type}
                                    </Badge>
                                  )}
                                  {position.activity.task_status && (
                                    <Badge
                                      variant={
                                        position.activity.task_status === 'done' ? 'success' :
                                          position.activity.task_status === 'in-progress' ? 'warning' :
                                            position.activity.task_status === 'cancelled' ? 'danger' :
                                              'default'
                                      }
                                      size="sm"
                                    >
                                      {position.activity.task_status}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Movement Info */}
                            {position.speed !== null && (
                              <div className="mt-2 flex items-center gap-3 text-[10px] text-gray-500">
                                <div className="flex items-center gap-1">
                                  <span>Speed:</span>
                                  <span className="font-medium text-gray-700">{position.speed.toFixed(1)} km/h</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span>{position.is_moving ? '🚗 Moving' : '⏸️ Stopped'}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                } else {
                  return (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 p-8">
                      <Activity className="h-12 w-12 mb-3" />
                      <p className="text-sm font-medium text-gray-500">No activity matching filters</p>
                      <p className="text-[11px] mt-1 text-center text-gray-500">Adjust your event filters to see more results</p>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
